import React, { useState } from 'react';

/**
 * Generates a consistent color from a user's initials (deterministic fallback).
 * Used when the user object doesn't have an avatarColor stored.
 */
const FALLBACK_COLORS = [
  '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3',
  '#00BCD4', '#009688', '#4CAF50', '#FF9800', '#FF5722',
  '#795548', '#607D8B', '#F44336', '#E040FB', '#536DFE',
  '#00E5FF', '#76FF03', '#FFD740', '#FF6E40', '#8D6E63',
];

function getColorFromName(firstName = '', lastName = '') {
  const str = `${firstName}${lastName}`.toLowerCase();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
}

/**
 * Reusable user avatar showing initials with a persistent color.
 *
 * Props:
 *   user       — { firstName, lastName, avatarColor? } (or null)
 *   firstName  — override first name
 *   lastName   — override last name
 *   size       — 'xs' | 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *   className  — extra classes
 *   rounded    — 'full' | 'xl' | 'lg'  (default 'full')
 */
const SIZE_MAP = {
  '2xs': 'w-5 h-5 text-[9px]',
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-7 h-7 text-xs',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-14 h-14 text-xl',
  '2xl': 'w-16 h-16 text-2xl',
};

const ROUNDED_MAP = {
  full: 'rounded-full',
  xl: 'rounded-xl',
  lg: 'rounded-lg',
};

const UserAvatar = ({
  user,
  firstName: fnOverride,
  lastName: lnOverride,
  size = 'md',
  className = '',
  rounded = 'full',
}) => {
  const [imgError, setImgError] = useState(false);
  const fn = fnOverride || user?.firstName || '';
  const ln = lnOverride || user?.lastName || '';
  const initials = `${fn?.[0] || ''}${ln?.[0] || ''}`.toUpperCase();
  const bgColor = user?.avatarColor || getColorFromName(fn, ln);
  const avatarUrl = user?.avatar;
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;
  const roundedClass = ROUNDED_MAP[rounded] || ROUNDED_MAP.full;

  if (avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt={`${fn} ${ln}`.trim()}
        title={`${fn} ${ln}`.trim()}
        className={`${sizeClass} ${roundedClass} object-cover flex-shrink-0 ${className}`}
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div
      className={`${sizeClass} ${roundedClass} flex items-center justify-center text-white font-bold flex-shrink-0 ${className}`}
      style={{ backgroundColor: bgColor }}
      title={`${fn} ${ln}`.trim()}
    >
      {initials}
    </div>
  );
};

export { getColorFromName, FALLBACK_COLORS };
export default UserAvatar;
